import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ListTodo,
  Plus,
  Filter,
  Trash2,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const taskTypes = [
  { value: "client_followup", label: "Client Follow-up" },
  { value: "document_collection", label: "Document Collection" },
  { value: "compliance_check", label: "Compliance Check" },
  { value: "lender_communication", label: "Lender Communication" },
  { value: "fee_collection", label: "Fee Collection" },
  { value: "general", label: "General" }
];

const priorityColors = {
  high: "bg-red-500",
  medium: "bg-amber-500",
  low: "bg-green-500"
};

// Preset tasks from initial lead to completion
const presetTasks = [
  { title: "Initial Lead Contact", task_type: "client_followup", priority: "high" },
  { title: "Discovery Call Scheduled", task_type: "client_followup", priority: "high" },
  { title: "Fact Find Appointment", task_type: "document_collection", priority: "high" },
  { title: "Document Collection Follow-up", task_type: "document_collection", priority: "medium" },
  { title: "Credit Check Review", task_type: "compliance_check", priority: "high" },
  { title: "Affordability Assessment", task_type: "compliance_check", priority: "high" },
  { title: "Lender Research", task_type: "lender_communication", priority: "medium" },
  { title: "Product Recommendation Prepared", task_type: "general", priority: "medium" },
  { title: "Client Presentation Meeting", task_type: "client_followup", priority: "high" },
  { title: "Application Submission", task_type: "lender_communication", priority: "high" },
  { title: "Valuation Booked", task_type: "lender_communication", priority: "medium" },
  { title: "Offer Received - Client Update", task_type: "client_followup", priority: "high" },
  { title: "Solicitor Instructed", task_type: "general", priority: "medium" },
  { title: "Exchange Progress Chase", task_type: "client_followup", priority: "medium" },
  { title: "Protection Review Appointment", task_type: "client_followup", priority: "medium" },
  { title: "Completion Day Confirmation", task_type: "client_followup", priority: "high" },
  { title: "Post-Completion Follow-up", task_type: "client_followup", priority: "low" },
  { title: "Fee Collection", task_type: "fee_collection", priority: "medium" }
];

export default function TaskManager() {
  const { user, getAuthHeaders } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [applications, setApplications] = useState([]);
  const [newTask, setNewTask] = useState({
    title: "", description: "", priority: "medium", task_type: "general", due_date: "", application_id: "none"
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      let url = `${API}/tasks`;
      if (filter !== "all") url += `?status=${filter}`;
      const [tasksRes, appsRes] = await Promise.all([
        axios.get(url, { headers: getAuthHeaders() }),
        axios.get(`${API}/applications`, { headers: getAuthHeaders() })
      ]);
      setTasks(tasksRes.data);
      setApplications(appsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const createTask = async () => {
    try {
      const taskData = {
        ...newTask,
        application_id: newTask.application_id === "none" ? null : (newTask.application_id || null)
      };
      await axios.post(`${API}/tasks`, taskData, { headers: getAuthHeaders() });
      toast.success("Task created!");
      setDialogOpen(false);
      setNewTask({ title: "", description: "", priority: "medium", task_type: "general", due_date: "", application_id: "none" });
      fetchData();
    } catch (error) {
      console.error("Task creation error:", error);
      toast.error(error.response?.data?.detail || "Failed to create task");
    }
  };

  const createPresetTasks = async (applicationId) => {
    try {
      for (const preset of presetTasks) {
        await axios.post(`${API}/tasks`, {
          ...preset,
          description: `Auto-generated task for application workflow`,
          application_id: applicationId || null
        }, { headers: getAuthHeaders() });
      }
      toast.success(`Created ${presetTasks.length} workflow tasks!`);
      setPresetDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Preset tasks error:", error);
      toast.error(error.response?.data?.detail || "Failed to create preset tasks");
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}`, { status }, { headers: getAuthHeaders() });
      toast.success("Task updated!");
      fetchData();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { headers: getAuthHeaders() });
      toast.success("Task deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="task-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold mb-2">Task Manager</h1>
            <p className="text-slate-400">Manage and track your daily tasks and follow-ups.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[150px] bg-[#0f172a] border-slate-700">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-slate-700">
                <SelectItem value="all">All Tasks</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Preset Tasks Button */}
            <Dialog open={presetDialogOpen} onOpenChange={setPresetDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10">
                  <Zap className="w-4 h-4 mr-2" /> Workflow Tasks
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0f172a] border-slate-700 max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Workflow Tasks</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <p className="text-sm text-slate-400">
                    This will create {presetTasks.length} pre-defined tasks for a complete mortgage workflow 
                    (from initial contact to completion).
                  </p>
                  <div className="space-y-2">
                    <Label>Select Application</Label>
                    <Select onValueChange={(value) => createPresetTasks(value)}>
                      <SelectTrigger className="bg-[#020617] border-slate-700">
                        <SelectValue placeholder="Choose an application" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0f172a] border-slate-700">
                        {applications.map(app => (
                          <SelectItem key={app.id} value={app.id}>
                            {app.client_name} - £{app.loan_amount?.toLocaleString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    <p className="text-xs text-slate-500 mb-2">Tasks to be created:</p>
                    {presetTasks.map((task, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                        <div className={`w-2 h-2 rounded-full ${priorityColors[task.priority]}`}></div>
                        {task.title}
                      </div>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-task-btn">
                  <Plus className="w-4 h-4 mr-2" /> New Task
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0f172a] border-slate-700">
                <DialogHeader>
                  <DialogTitle>Create New Task</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Task Title</Label>
                    <Input
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                      placeholder="Enter task title"
                      className="bg-[#020617] border-slate-700"
                      data-testid="new-task-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newTask.description}
                      onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                      placeholder="Task description..."
                      className="bg-[#020617] border-slate-700"
                      rows={3}
                      data-testid="new-task-description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Link to Application (Optional)</Label>
                    <Select value={newTask.application_id} onValueChange={(v) => setNewTask({ ...newTask, application_id: v })}>
                      <SelectTrigger className="bg-[#020617] border-slate-700">
                        <SelectValue placeholder="Select application" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0f172a] border-slate-700">
                        <SelectItem value="none">No application</SelectItem>
                        {applications.map(app => (
                          <SelectItem key={app.id} value={app.id}>{app.client_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                        <SelectTrigger className="bg-[#020617] border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-slate-700">
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Task Type</Label>
                      <Select value={newTask.task_type} onValueChange={(v) => setNewTask({ ...newTask, task_type: v })}>
                        <SelectTrigger className="bg-[#020617] border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-slate-700">
                          {taskTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={newTask.due_date}
                      onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                      className="bg-[#020617] border-slate-700"
                      data-testid="new-task-due-date"
                    />
                  </div>
                  <Button onClick={createTask} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-new-task">
                    Create Task
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-[#0f172a] border-slate-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Pending</p>
                <p className="text-xl font-bold font-mono">{pendingTasks.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0f172a] border-slate-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Completed</p>
                <p className="text-xl font-bold font-mono">{completedTasks.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0f172a] border-slate-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-slate-400">High Priority</p>
                <p className="text-xl font-bold font-mono">{tasks.filter(t => t.priority === 'high' && t.status === 'pending').length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tasks List */}
        <Card className="bg-[#0f172a] border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg">Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-slate-800 animate-pulse rounded-lg"></div>
                ))}
              </div>
            ) : tasks.length === 0 ? (
              <div className="py-12 text-center">
                <ListTodo className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No tasks found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map((task) => {
                  const linkedApp = applications.find(a => a.id === task.application_id);
                  return (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex items-center gap-4 p-4 rounded-lg bg-[#020617] border border-slate-800 ${
                        task.status === 'completed' ? 'opacity-60' : ''
                      }`}
                    >
                      <Checkbox
                        checked={task.status === 'completed'}
                        onCheckedChange={(checked) => updateTaskStatus(task.id, checked ? 'completed' : 'pending')}
                        data-testid={`task-checkbox-${task.id}`}
                      />
                      <div className={`w-2 h-2 rounded-full ${priorityColors[task.priority]}`}></div>
                      <div className="flex-1">
                        <p className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-500' : ''}`}>
                          {task.title}
                        </p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {taskTypes.find(t => t.value === task.task_type)?.label || task.task_type}
                          </Badge>
                          {linkedApp && (
                            <span className="text-xs text-blue-400">
                              {linkedApp.client_name}
                            </span>
                          )}
                          {task.due_date && (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {task.due_date}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteTask(task.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
